/** A dry-run of an event card on target banks: the card as teams would read it, eligibility, and the impact of each answer (`eventInjectionPreview`). */
import type { BoardTeamRow, EventCardView, ResponseOf } from "@bsim/contracts";
import { KPI_DEFS, type KpiId } from "@bsim/domain";
import { kpiLabel, sentenceCase } from "../format/labels";
import { formatKpi } from "../format";
import { useState } from "react";
import { GxPill } from "../theme/GxPill";

export type Preview = ResponseOf<"eventInjectionPreview">;

/** The api's refusal names the box in its own lowercase words; the dialog's label reads "Show it even if this bank would not be affected", so the sentence says that. */
export const reasonWords = (reason: string): string => reason.replace(/'bypass trigger'/i, "'Show it even if this bank would not be affected'");

/** The participant card: title, narrative, the choices with their trade-offs and what happens with no answer. */
export function EventCard({ card }: { readonly card: EventCardView }) {
  return (
    <div className="gx-fac-evcard" data-testid="preview.card">
      <div className="gx-eyebrow">{card.category} · severity {card.severity}</div>
      <h3 className="gx-h3">{sentenceCase(card.title)}</h3>
      {card.narrative.map((p) => <p key={p} className="gx-small gx-muted">{p}</p>)}
      <ul className="gx-fac-choices">
        {card.choices.map((c) => (
          <li key={c.id}><b>{c.label}</b> {c.summary === c.label ? null : <span className="gx-muted">{c.summary}</span>}<br /><span className="gx-small gx-faint">{c.tradeOffs.join(" · ")}</span></li>
        ))}
      </ul>
      <div className="gx-small gx-faint">If no answer: {card.defaultChoice.label}</div>
    </div>
  );
}

const kpiIds = (impact: Preview["perTeam"][number]["impact"]): KpiId[] => (Object.keys(impact.baseline) as KpiId[]).filter((k) => k in KPI_DEFS);

/** One line per team: the share price in the preview's horizon at the baseline, if nobody answers and across the answers (the figures a facilitator reads before holding); a card with no price reading says how many answers it has. */
function summaryOf(t: Preview["perTeam"][number]): string {
  const price = t.impact.baseline["price"];
  if (price === undefined) return `${t.card.choices.length} answers; the full figures are under "Show every figure".`;
  const across = t.card.choices.flatMap((c) => { const v = t.impact.byChoice[c.id]?.["price"]; return v === undefined ? [] : [v]; });
  const range = across.length === 0 ? "" : `; the answers run from ${formatKpi("price", Math.min(...across))} to ${formatKpi("price", Math.max(...across))}`;
  return `Share price in ${t.impact.horizonMonths} months: ${formatKpi("price", price)} as things stand, ${formatKpi("price", t.impact.withDefault["price"] ?? NaN)} if nobody answers${range}.`;
}

/** A short summary per team (eligibility and one price line); every card, narrative and figure sits behind "Show every figure" so the Hold button stays in view. */
export function EventPreviewList({ preview, teams }: { readonly preview: Preview; readonly teams: readonly BoardTeamRow[] }) {
  const [all, setAll] = useState(false);
  const nameOf = (id: string): string => teams.find((x) => x.teamId === id)?.name ?? "Team";
  return (
    <div className="gx-fac-previews" data-testid="preview.list">
      <ul className="gx-fac-list gx-fac-prevsum" data-testid="preview.summary">
        {preview.perTeam.map((t) => (
          <li key={t.teamId}><b>{nameOf(t.teamId)}</b>{t.eligible ? <GxPill tone="good">Eligible</GxPill> : <GxPill tone="warn">Not eligible</GxPill>}
            {t.eligible || t.ineligibleReason === null ? null : <span className="gx-small gx-muted">{reasonWords(t.ineligibleReason)}</span>}
            <span className="gx-small gx-muted">{summaryOf(t)}</span></li>
        ))}
      </ul>
      <button type="button" className="gx-btn gx-btn--ghost" aria-expanded={all} data-testid="preview.all" onClick={() => setAll((v) => !v)}>{all ? "Hide the figures" : "Show every figure"}</button>
      {!all ? null : preview.perTeam.map((t) => {
        const kpis = kpiIds(t.impact);
        const choices = t.card.choices;
        return (
          <section key={t.teamId} className="gx-fac-preview">
            <div className="gx-fac-preview-h">
              <b>{teams.find((x) => x.teamId === t.teamId)?.name ?? "Team"}</b>
              {t.eligible ? <GxPill tone="good">Eligible</GxPill> : <GxPill tone="warn">Not eligible{t.ineligibleReason === null ? "" : ` · ${reasonWords(t.ineligibleReason)}`}</GxPill>}
            </div>
            <EventCard card={t.card} />
            {kpis.length === 0 ? null : (
              <div className="gx-fac-impact-wrap" role="region" aria-label="Impact by answer, scrolls sideways" tabIndex={0}><table className="gx-table gx-fac-impact" aria-label="Impact by answer">
                <thead><tr><th>In {t.impact.horizonMonths} months</th><th>Baseline</th><th>Default</th>{choices.map((c) => <th key={c.id}>{c.label}</th>)}</tr></thead>
                <tbody>
                  {kpis.map((k) => (
                    <tr key={k}>
                      <th scope="row">{kpiLabel(k)}</th>
                      <td className="gx-num">{formatKpi(k, t.impact.baseline[k] ?? NaN)}</td>
                      <td className="gx-num">{formatKpi(k, t.impact.withDefault[k] ?? NaN)}</td>
                      {choices.map((c) => <td key={c.id} className="gx-num">{formatKpi(k, t.impact.byChoice[c.id]?.[k] ?? NaN)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>
        );
      })}
    </div>
  );
}
